/**
 * WeaveIn Staff v2 — Design Index
 * Overview page showing all 8 redesign screens with card grid and handoff info.
 * Matches mocks/index.html pixel-for-pixel.
 */
// Cards are non-navigable until v2 pages are implemented

const MOCK_CARDS: {
  num: string
  route: string
  eyebrow: string
  title: string
  description: string
  meta: string
  thumb: 'shell' | 'cmHome' | 'supervisor' | 'casesList' | 'caseDetail' | 'referrals' | 'comms' | 'access'
}[] = [
  {
    num: '01',
    route: '/app',
    eyebrow: 'App Shell',
    title: 'Sidebar + top chrome',
    description:
      'Role-shaped nav with 4 groups (Promotora view) or 7 (admin). Visible \u2318K, presence footer, counts replacing notifications tray.',
    meta: 'Replaces SidebarNavigation.tsx',
    thumb: 'shell',
  },
  {
    num: '02',
    route: '/app/home',
    eyebrow: 'Case Manager Home',
    title: 'My day',
    description:
      '\u201cStart here\u201d focus bar, tasks in time order, weekly rhythm panel. Replaces the 4-goals-of-equal-weight Promotora dashboard.',
    meta: 'Replaced PromotoraDashboard.tsx (removed)',
    thumb: 'cmHome',
  },
  {
    num: '03',
    route: '/app/supervisor',
    eyebrow: 'Supervisor Dashboard',
    title: 'Team overview',
    description:
      '\u201c3 things need you today\u201d hero band, two-panel grid (Team health + Review queue), drill-in from every row.',
    meta: 'Replaced SupervisorDashboard.tsx (removed)',
    thumb: 'supervisor',
  },
  {
    num: '04',
    route: '/app/cases',
    eyebrow: 'Cases \u2014 list',
    title: 'Members & cases index',
    description:
      'Saved-view tabs (My cases / Team / Unassigned / All), filters, compact dense table. Split from the monolithic workspace.',
    meta: 'Splits CaseWorkspace.tsx:1-800',
    thumb: 'casesList',
  },
  {
    num: '05',
    route: '/app/cases/1',
    eyebrow: 'Cases \u2014 detail',
    title: 'One case, one screen',
    description:
      'Next-best-action bar, left rail instead of tabs, right context rail. No gradient chrome.',
    meta: 'Splits CaseWorkspace.tsx:800-2094',
    thumb: 'caseDetail',
  },
  {
    num: '06',
    route: '/app/referrals',
    eyebrow: 'Referrals',
    title: 'Flattened queue',
    description:
      '6 nav items \u2192 1 page with saved views (Unmatched, Auto-matched, My queue, Needs review). One URL, one mental model.',
    meta: 'Consolidates 6 pages',
    thumb: 'referrals',
  },
  {
    num: '07',
    route: '/app/communications',
    eyebrow: 'Communications',
    title: 'Unified inbox',
    description:
      'Merges Inbox, Messaging Workspace, Campaigns, Chatflows. Conversation list + thread + tabs for campaigns & flows.',
    meta: 'Consolidates 4 pages',
    thumb: 'comms',
  },
  {
    num: '08',
    route: '/app/admin/access',
    eyebrow: 'Admin \u00b7 Access',
    title: 'Unified RBAC',
    description:
      'One page replaces five. Role \u2192 capabilities, inheritance visible, \u201cpreview as\u201d button.',
    meta: 'Replaces 5 admin pages',
    thumb: 'access',
  },
]

/* ---------- Mini thumbnail wireframes (CSS-only) ---------- */

function ThumbShell() {
  return (
    <div className="grid h-full w-[92%] grid-cols-[44px_1fr] overflow-hidden rounded-md border border-v2border bg-white">
      <div className="flex flex-col gap-[3px] border-r border-v2border bg-paper px-1 py-2">
        <div className="ml-1 mt-1.5 mb-0.5 h-[3px] w-[60%] rounded-sm bg-ink-400" />
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-ink" />
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="ml-1 mt-1.5 mb-0.5 h-[3px] w-[60%] rounded-sm bg-ink-400" />
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-v2border" />
      </div>
      <div className="flex flex-col gap-[5px] p-2.5">
        <div className="h-2 w-[30%] rounded-sm bg-v2border" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
      </div>
    </div>
  )
}

function ThumbCmHome() {
  return (
    <div className="grid h-full w-[92%] grid-cols-[44px_1fr] overflow-hidden rounded-md border border-v2border bg-white">
      <div className="flex flex-col gap-[3px] border-r border-v2border bg-paper px-1 py-2">
        <div className="h-1 rounded-sm bg-ink" />
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-v2border" />
      </div>
      <div className="flex flex-col gap-[5px] p-2.5">
        <div className="h-2 w-[60%] rounded-sm bg-ink" />
        <div className="h-[18px] rounded-[3px] bg-ink" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
      </div>
    </div>
  )
}

function ThumbSupervisor() {
  return (
    <div className="grid h-full w-[92%] grid-cols-[44px_1fr] overflow-hidden rounded-md border border-v2border bg-white">
      <div className="flex flex-col gap-[3px] border-r border-v2border bg-paper px-1 py-2">
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-ink" />
        <div className="h-1 rounded-sm bg-v2border" />
      </div>
      <div className="flex flex-1 flex-col gap-[5px] p-2.5">
        <div className="h-2 w-[30%] rounded-sm bg-v2border" />
        <div className="h-3.5 rounded-r-[3px] border-l-2 border-danger bg-danger-soft" />
        <div className="grid flex-1 grid-cols-2 gap-1">
          <div className="flex flex-col gap-[3px] rounded-[3px] border border-v2border-muted bg-paper p-[5px]">
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
          </div>
          <div className="flex flex-col gap-[3px] rounded-[3px] border border-v2border-muted bg-paper p-[5px]">
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
          </div>
        </div>
      </div>
    </div>
  )
}

function ThumbCasesList() {
  return (
    <div className="grid h-full w-[92%] grid-cols-[44px_1fr] overflow-hidden rounded-md border border-v2border bg-white">
      <div className="flex flex-col gap-[3px] border-r border-v2border bg-paper px-1 py-2">
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-ink" />
        <div className="h-1 rounded-sm bg-v2border" />
      </div>
      <div className="flex flex-col gap-[5px] p-2.5">
        <div className="h-2 w-[30%] rounded-sm bg-v2border" />
        <div className="grid grid-cols-3 gap-1">
          <div className="h-3.5 rounded-sm bg-paper-2" />
          <div className="h-3.5 rounded-sm bg-paper-2" />
          <div className="h-3.5 rounded-sm bg-paper-2" />
        </div>
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
      </div>
    </div>
  )
}

function ThumbCaseDetail() {
  return (
    <div className="grid h-full w-[92%] grid-cols-[44px_1fr] overflow-hidden rounded-md border border-v2border bg-white">
      <div className="flex flex-col gap-[3px] border-r border-v2border bg-paper px-1 py-2">
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-ink" />
        <div className="h-1 rounded-sm bg-v2border" />
      </div>
      <div className="flex flex-col gap-[5px] p-2.5">
        <div className="h-2 w-[30%] rounded-sm bg-v2border" />
        <div className="h-[18px] rounded-[3px] bg-ink" />
        <div className="grid grid-cols-3 gap-1">
          <div className="h-3.5 rounded-sm bg-paper-2" />
          <div className="h-3.5 rounded-sm bg-paper-2" />
          <div className="h-3.5 rounded-sm bg-paper-2" />
        </div>
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
      </div>
    </div>
  )
}

function ThumbReferrals() {
  return (
    <div className="grid h-full w-[92%] grid-cols-[44px_1fr] overflow-hidden rounded-md border border-v2border bg-white">
      <div className="flex flex-col gap-[3px] border-r border-v2border bg-paper px-1 py-2">
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-ink" />
      </div>
      <div className="flex flex-col gap-[5px] p-2.5">
        <div className="h-2 w-[30%] rounded-sm bg-v2border" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
      </div>
    </div>
  )
}

function ThumbComms() {
  return (
    <div className="grid h-full w-[92%] grid-cols-[44px_1fr] overflow-hidden rounded-md border border-v2border bg-white">
      <div className="flex flex-col gap-[3px] border-r border-v2border bg-paper px-1 py-2">
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-ink" />
        <div className="h-1 rounded-sm bg-v2border" />
      </div>
      <div className="flex flex-1 flex-col gap-[5px] p-2.5">
        <div className="h-2 w-[30%] rounded-sm bg-v2border" />
        <div className="grid flex-1 grid-cols-2 gap-1">
          <div className="flex flex-col gap-[3px] rounded-[3px] border border-v2border-muted bg-paper p-[5px]">
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
          </div>
          <div className="flex flex-col gap-[3px] rounded-[3px] border border-v2border-muted bg-paper p-[5px]">
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
            <div className="h-1.5 rounded-sm bg-v2border-muted" />
          </div>
        </div>
      </div>
    </div>
  )
}

function ThumbAccess() {
  return (
    <div className="grid h-full w-[92%] grid-cols-[44px_1fr] overflow-hidden rounded-md border border-v2border bg-white">
      <div className="flex flex-col gap-[3px] border-r border-v2border bg-paper px-1 py-2">
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-v2border" />
        <div className="h-1 rounded-sm bg-ink" />
      </div>
      <div className="flex flex-col gap-[5px] p-2.5">
        <div className="h-2 w-[30%] rounded-sm bg-v2border" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
        <div className="grid grid-cols-3 gap-1">
          <div className="h-3.5 rounded-sm bg-paper-2" />
          <div className="h-3.5 rounded-sm bg-paper-2" />
          <div className="h-3.5 rounded-sm bg-paper-2" />
        </div>
        <div className="h-1.5 rounded-sm bg-v2border-muted" />
      </div>
    </div>
  )
}

const THUMBS: Record<string, () => JSX.Element> = {
  shell: ThumbShell,
  cmHome: ThumbCmHome,
  supervisor: ThumbSupervisor,
  casesList: ThumbCasesList,
  caseDetail: ThumbCaseDetail,
  referrals: ThumbReferrals,
  comms: ThumbComms,
  access: ThumbAccess,
}

export default function DesignIndex() {
  return (
    <div className="min-h-screen bg-paper font-sans antialiased">
      <div className="mx-auto max-w-[1200px] px-12 pt-[60px] pb-[120px]">
        {/* Lede */}
        <div className="max-w-[820px]">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500">
            WeaveIn Staff Redesign &middot; v2 mocks
          </span>
          <h1 className="mt-2 font-display text-[44px] font-semibold leading-[1.05] tracking-[-0.03em] text-ink">
            Ready for implementation.
          </h1>
          <p className="mt-4 text-[17px] leading-[1.5] text-ink-700">
            Eight high-fidelity mocks mapping directly to routes and components
            in the existing codebase. Each page shows the target UI plus an
            annotated redline mode (toggle in the top bar) with component-level
            references for Cursor / Claude Code handoff.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <span className="inline-flex h-[30px] items-center gap-1.5 rounded-[var(--v2-r)] bg-ink-400 px-3 text-xs font-medium text-white cursor-default">
              Pages coming soon
            </span>
            <span className="font-mono text-[11px] tracking-[0.02em] text-ink-500 ml-auto">
              Implementation in progress &mdash; screens listed below.
            </span>
          </div>
        </div>

        {/* Section header */}
        <div className="mt-[60px] mb-5 flex items-baseline justify-between border-b border-v2border pb-3">
          <h2 className="font-display text-[22px] font-semibold text-ink">
            The eight pages
          </h2>
          <div className="flex gap-5 text-xs text-ink-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex items-center gap-2 text-[11px] text-ink-500">
                <span className="h-1.5 w-1.5 rounded-full bg-success" />
              </span>
              Net-new UI
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex items-center gap-2 text-[11px] text-ink-500">
                <span className="h-1.5 w-1.5 rounded-full bg-attention" />
              </span>
              Replaces existing
            </span>
          </div>
        </div>

        {/* Card grid */}
        <div className="grid grid-cols-2 gap-3.5">
          {MOCK_CARDS.map((card) => {
            const Thumb = THUMBS[card.thumb]
            return (
              <div
                key={card.num}
                className="group flex flex-col overflow-hidden rounded-[var(--v2-r-xl)] border border-v2border bg-white transition-[border-color,transform] duration-[120ms] hover:-translate-y-px hover:border-ink-400 cursor-default"
              >
                {/* Thumbnail */}
                <div className="relative flex h-[180px] items-center justify-center overflow-hidden border-b border-v2border bg-paper">
                  <span className="font-display text-[96px] font-medium leading-none tracking-[-0.04em] text-ink-300">
                    {card.num}
                  </span>
                  <div className="absolute inset-[18px] flex items-center justify-center">
                    <Thumb />
                  </div>
                </div>

                {/* Body */}
                <div className="flex flex-col gap-1.5 px-[22px] pt-5 pb-[22px]">
                  <span className="mb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500">
                    {card.eyebrow}
                  </span>
                  <h3 className="font-display text-[20px] font-semibold tracking-[-0.02em] text-ink">
                    {card.title}
                  </h3>
                  <p className="text-[13px] leading-[1.5] text-ink-700">
                    {card.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <span className="inline-flex h-[22px] items-center rounded-full border border-v2border bg-paper-2 px-2 font-mono text-[10px] text-ink-500">
                      {card.route}
                    </span>
                    <span className="inline-flex h-[22px] items-center gap-[5px] rounded-full border border-v2border bg-paper px-2 text-[11px] font-medium text-ink-700">
                      {card.meta.includes('Replaces') ||
                      card.meta.includes('Splits') ? (
                        <>
                          {card.meta.split(/(?=\s\S+\.tsx)/).map((part, i) =>
                            part.match(/\S+\.tsx\S*/) ? (
                              <code
                                key={i}
                                className="font-mono text-[10px]"
                              >
                                {part.trim()}
                              </code>
                            ) : (
                              <span key={i}>{part}</span>
                            ),
                          )}
                        </>
                      ) : (
                        card.meta
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Handoff section */}
        <div className="mt-20 grid grid-cols-[2fr_3fr] gap-12 rounded-[var(--v2-r-xl)] bg-ink p-10 text-white">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/55">
              For engineering
            </span>
            <h2 className="mt-3 font-display text-[28px] font-semibold tracking-[-0.02em] text-white">
              Built to hand off.
            </h2>
            <p className="mt-3 text-sm leading-[1.5] text-white/70">
              These mocks are static HTML with tokens mapped to the existing
              Tailwind config. Toggle redlines in the top bar to see component
              references, spec values, and state notes.
            </p>
            <span className="mt-5 inline-flex h-[30px] items-center gap-1.5 rounded-[var(--v2-r)] border border-white bg-white px-3 text-xs font-medium text-ink cursor-default">
              Handoff doc &mdash; see HANDOFF.md in repo
            </span>
          </div>
          <div>
            <ul className="flex list-none flex-col gap-2.5 p-0">
              {[
                {
                  bold: 'Token-compatible',
                  text: (
                    <>
                      colors use <Code>oklch()</Code>; mapping to Tailwind theme
                      extension is in <Code>HANDOFF.md</Code>.
                    </>
                  ),
                },
                {
                  bold: 'Component-aligned',
                  text: 'every section annotates which existing file it replaces or modifies (shown in redline mode).',
                },
                {
                  bold: 'State-complete',
                  text: 'empty, loading, permission-denied variants toggleable via URL params.',
                },
                {
                  bold: 'Realistic data',
                  text: 'CalAIM program names, real SDOH categories, plausible Medi-Cal workflow labels.',
                },
                {
                  bold: 'Navigation works',
                  text: 'click between screens; state persists via localStorage.',
                },
              ].map((item) => (
                <li
                  key={item.bold}
                  className="relative pl-4 text-[13px] leading-[1.5] text-white/85"
                >
                  <span className="absolute left-0 top-[9px] h-px w-2 bg-white/50" />
                  <b className="font-semibold">{item.bold}</b> &mdash;{' '}
                  {item.text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded-[3px] bg-white/10 px-1.5 py-px font-mono text-[11px] text-white/90">
      {children}
    </code>
  )
}
